import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormDatePicker } from '@portal_components/components/FormDatePicker';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { TableItem } from '@portal_components/components/TableItem';
import { Contract, EPageViewType } from '../../types';
import { useContractStandingData } from '../../hooks/useContractStandingData';
import { Button } from '@portal_components/components/Button';
import { ServiceSiteDetailsListItem } from './ServiceSiteDetailsListItem';
import { FormCard } from '@portal_components/components/FormCard';

interface ServiceListItemProps {
  readonly: boolean;
  index: number;
  viewType?: EPageViewType;
  expandedPricingMatrix: string | null;
  setExpandedPricingMatrix: (expandedPricingMatrix: string | null) => void;
  removeService: (index: number) => void;
}

export const ServiceListItem = ({ index, readonly, viewType, expandedPricingMatrix, setExpandedPricingMatrix, removeService }: ServiceListItemProps) => {
  const {
    standingData: { standingDataServiceCodeList }
  } = useContractStandingData();
  const { watch, setValue, getValues } = useFormContext<Contract>();
  const [srvCodeId, markedForDeletion] = watch([`serviceList.${index}.srvCodeId`, `serviceList.${index}.markedForDeletion`]);

  const { fields, append, remove } = useFieldArray<Contract, `serviceList.${number}.siteDetails`>({
    name: `serviceList.${index}.siteDetails`
  });

  const handleRemove = () => {
    if (getValues(`serviceList.${index}.serviceId`)) {
      setValue(`serviceList.${index}.markedForDeletion`, !markedForDeletion);
      return;
    }
    removeService(index);
  };

  return (
    <div className="flex flex-col w-full pl-2" key={`ServiceStandardPrice-${index}`}>
      {markedForDeletion && <p className="text-feedback-orange text-sm">Service marked for deletion</p>}
      <TableItem remove={handleRemove} readonly={readonly}>
        <FormDropdown<Contract>
          title="Service Code"
          name={`serviceList.${index}.srvCodeId`}
          placeholder="Select..."
          elementsForDisplay={standingDataServiceCodeList}
          readonly={readonly || markedForDeletion}
        />
        <FormDatePicker<Contract> title="Start Date" name={`serviceList.${index}.srvStartDate`} readonly={readonly || markedForDeletion} mandatory />
        <FormDatePicker<Contract> title="End Date" name={`serviceList.${index}.srvEndDate`} readonly={readonly || markedForDeletion} />
      </TableItem>
      {!!srvCodeId && (
        <FormCard borderLeftOnly>
          <div className="flex flex-row justify-start py-2 mb-4">{!readonly && <Button text="Add Site Detail" primary onClick={() => append({ newSiteDetail: true })} />}</div>
          {fields.length > 0 &&
            fields.map((_, siteDetailIndex) => (
              <ServiceSiteDetailsListItem
                key={`ServiceStandardPriceTable-${siteDetailIndex}`}
                siteDetailIndex={siteDetailIndex}
                serviceIndex={index}
                viewType={viewType}
                readonly={readonly || !!markedForDeletion}
                expandedPricingMatrix={expandedPricingMatrix}
                setExpandedPricingMatrix={setExpandedPricingMatrix}
                removeSiteDetail={remove}
              />
            ))}
          {fields.length === 0 && <p className="text-center">No site details</p>}
        </FormCard>
      )}
    </div>
  );
};
